// CBW (Control By Web) API client
// All calls routed through access-api-v2 via connector-api trust boundary.
// Auth: Bearer token from localStorage (mirrors apiClient.ts pattern).

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.intrlock.io';

function getToken(): string {
  return localStorage.getItem('intrlock_token') || '';
}

async function cbwFetch(urlPath: string, options: RequestInit = {}): Promise<any> {
  const res = await fetch(`${API_BASE}${urlPath}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${getToken()}`,
      ...(options.headers || {}),
    },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`CBW API error ${res.status}: ${text}`);
  }
  if (res.status === 204) return null;
  return res.json();
}

// ---------- Provision -------------------------------------------------------

export interface CbwProvisionParams {
  property_id: string;
  display_name: string;
  serial?: string;
  entry_point?: 'devices' | 'access-control';
  acs_system_id?: string;
}

export interface CbwProvisionResult {
  device_id: string;
  iot_thing_name: string;
  bundle_token: string;
  bundle_url: string;
  cert_expires_at: string;
  acs_system_id?: string;
}

/**
 * POST /cbw/provision — creates cbw_devices row + IoT Thing + cert
 * Returns bundle_token for zip download and provisioned device metadata.
 */
export async function provisionCbw(params: CbwProvisionParams): Promise<CbwProvisionResult> {
  return cbwFetch('/cbw/provision', {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

// ---------- Bundle download -------------------------------------------------

/**
 * Returns the direct download URL for the provisioning zip bundle.
 * Use as anchor href with the `download` attribute — no auth needed
 * (token embedded in path, served as attachment by access-api-v2).
 */
export function getBundleUrl(token: string): string {
  return `${API_BASE}/cbw/bundle/${token}`;
}

// ---------- Push config (Path A) -------------------------------------------

export interface CbwPushConfigParams {
  device_id: string;
  cbw_url: string;
  cbw_admin_user: string;
  cbw_admin_password: string;
}

/**
 * POST /cbw/push-config — optional Path A: push MQTT config to device
 * directly if it's already reachable on the LAN.
 */
export async function pushCbwConfig(params: CbwPushConfigParams): Promise<any> {
  return cbwFetch('/cbw/push-config', {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

// ---------- Device actions --------------------------------------------------

export interface CbwActionParams {
  device_id: string;
  action: 'pulse_relay' | 'set_relay' | 'get_state' | 'reboot';
  args?: Record<string, any>;
}

/**
 * POST /cbw/devices/:id/actions/:action — control relay, get state, reboot.
 */
export async function cbwAction(params: CbwActionParams): Promise<any> {
  return cbwFetch(`/cbw/devices/${params.device_id}/actions/${params.action}`, {
    method: 'POST',
    body: JSON.stringify(params.args || {}),
  });
}

// ---------- Device status ---------------------------------------------------

export interface CbwStatus {
  online: boolean;
  cert_rotation_status: string | null;
  last_seen_at: string | null;
  latest_state: Record<string, any> | null;
}

/**
 * GET /cbw/devices/:id/status — live device status from IoT ingest.
 */
export async function getCbwStatus(device_id: string): Promise<CbwStatus> {
  return cbwFetch(`/cbw/devices/${device_id}/status`, { method: 'GET' });
}
